import React from 'react';
import { Link } from 'react-router-dom';
import styles from './MarketingPage.module.css';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useReveal from '../hooks/useReveal';

const CONTACT_EMAIL = 'info@velox-network.com';

const BOUNDARIES = [
  {
    term: 'Private notes stay private',
    body: 'Notes written against a contact belong to the person who wrote them. They are not returned to company administrators, in the contact hub or anywhere else.',
  },
  {
    term: 'Locations are not exposed to administrators',
    body: 'A contact records where it was made so the individual can recall the meeting. In company views this is reduced to a simple indicator of whether a location exists, never the coordinates themselves.',
  },
  {
    term: 'Consent is captured on joining',
    body: 'When someone accepts an invitation to a company workspace, their agreement to company visibility of the contacts they capture is recorded with a timestamp.',
  },
  {
    term: 'Bulk access is logged',
    body: 'Every time an administrator views contact information in bulk, that access is written to a security audit log attributed to them.',
  },
];

const PLATFORM = [
  {
    term: 'Authentication',
    body: 'Passwords are hashed, never stored in readable form. New accounts are confirmed by an emailed verification code before they become active.',
  },
  {
    term: 'Sessions',
    body: 'Access tokens are short-lived and paired with rotating refresh tokens, so a captured token has a narrow window of use and can be revoked.',
  },
  {
    term: 'Abuse protection',
    body: 'Authentication endpoints (sign-in, registration, verification and password reset) are individually rate limited to frustrate brute-force attempts.',
  },
  {
    term: 'Transport and headers',
    body: 'Traffic is served over HTTPS, with a hardened set of HTTP response headers including a content security policy.',
  },
  {
    term: 'Your data on request',
    body: 'You can delete your account from within the application. Our privacy policy sets out what is retained, for how long, and how to exercise your rights under UK data protection law.',
  },
];

const SecurityPrivacyPage = () => {
  const revealRef = useReveal();

  useDocumentMeta({
    title: 'Security and privacy',
    description:
      'How Velox protects contact data: private notes stay private, administrator access is audit-logged, consent is captured on joining, and sessions use short-lived rotating tokens.',
    path: '/security-privacy',
  });

  return (
    <div ref={revealRef}>
      <section className={styles.pageHero}>
        <div className="vxm-grid-motif" />
        <div className={`vxm-container vxm-container--narrow ${styles.pageHeroInner}`}>
          <p className="vxm-eyebrow">Security &amp; privacy</p>
          <h1 className="vxm-h1">Where the line sits, and why</h1>
          <p className="vxm-lead">
            A company contact hub only works if the people filling it trust it.
            This page sets out exactly what an employer can and cannot see, and
            the controls behind it.
          </p>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">What your employer can and cannot see</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-6)' }}>
              A company account gives administrators visibility of the business
              relationships their team has built. It does not give them your
              private working notes or your movements.
            </p>
          </div>
          <ul className={styles.detailList}>
            {BOUNDARIES.map((item) => (
              <li key={item.term} className={`${styles.detailRow} vxm-reveal`}>
                <h3 className={styles.detailTerm}>{item.term}</h3>
                <p className={styles.detailBody}>{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">Platform controls</h2>
          </div>
          <ul className={styles.detailList} style={{ marginTop: 'var(--vxm-space-6)' }}>
            {PLATFORM.map((item) => (
              <li key={item.term} className={`${styles.detailRow} vxm-reveal`}>
                <h3 className={styles.detailTerm}>{item.term}</h3>
                <p className={styles.detailBody}>{item.body}</p>
              </li>
            ))}
          </ul>

          <div className={`${styles.noteBox} vxm-reveal`}>
            <p>
              We describe the controls we have actually implemented, and we do
              not claim security certifications we do not hold. If your
              procurement process needs specific assurances, write to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="vxm-link">
                {CONTACT_EMAIL}
              </a>{' '}
              and we will answer directly.
            </p>
          </div>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className={`${styles.panel} vxm-reveal`}>
            <h2 className={styles.panelTitle}>Full policy documents</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-5)' }}>
              Our privacy policy and terms of service set out data handling,
              retention, sub-processors and your rights in full.
            </p>
            <Link to="/policy/privacy" className="vxm-btn vxm-btn--secondary">
              Read the privacy policy and terms
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SecurityPrivacyPage;
